"use client";

import { Mail } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Faq() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const faqs = [{
    category: "Process",
    question: "How do I start my bathroom remodel in Lakeland, FL?",
    answer: "Getting started is simple. Call or submit the quote form with details about your bathroom remodel in Lakeland, FL, including what you want updated, your timeline, and any specific features you are considering.\n\n"
  }, {
    category: "Timeline",
    question: "How long does a typical bathroom renovation take?",
    answer: "Most bathroom remodels in the Lakeland area are completed within 2 to 4 weeks, depending on the scope of work. We prioritize efficient scheduling to minimize disruption to your daily routine."
  }, {
    category: "Cost",
    question: "What factors influence the cost of a bathroom remodel?",
    answer: "Costs vary based on the size of the bathroom, the quality of materials selected, and the complexity of plumbing or electrical changes. We work with you to find solutions that fit your budget."
  }, {
    category: "Design",
    question: "Can you help with bathroom design and material selection?",
    answer: "Yes. We connect you with experts who assist in selecting high-quality tiles, fixtures, and vanities that match your aesthetic goals, ensuring a cohesive and beautiful final result for your home."
  }, {
    category: "Service Area",
    question: "Do you serve all of Polk County?",
    answer: "We proudly serve homeowners throughout Lakeland and the surrounding Polk County communities. Contact us today to confirm if we are currently operating in your specific neighborhood."
  }, {
    category: "Quality",
    question: "Are bathroom remodel warranties available?",
    answer: "Warranty options can vary depending on the remodeling business, project type, materials, and scope of work. When you request a bathroom remodel quote, ask what labor or material warranties may be available for your project.\n\n"
  }, {
    category: "Permits",
    question: "Are permits needed for a bathroom remodel in Lakeland, FL?",
    answer: "Permit requirements can depend on the scope of the bathroom remodel. Projects involving plumbing, electrical, structural changes, or major alterations may require permits through the City of Lakeland or Polk County. When you request a quote, ask what permits may be needed for your specific project.\n\n"
  }, {
    category: "Preparation",
    question: "How should I prepare my home for the renovation?",
    answer: "We recommend clearing personal items from the bathroom and creating a clear path for our team. We take care of protecting your floors and surrounding areas during the construction process."
  }, {
    category: "Consultation",
    question: "Can I request a free bathroom remodel quote?",
    answer: "Yes. You can call or submit the quote form to request a free bathroom remodel quote in Lakeland, FL. Share a few details about your project, and you can review next steps with a local bathroom remodel professional.\n\n"
  }, {
    category: "Teams",
    question: "Can I request a quote for a master bathroom remodel?",
    answer: "Yes. You can request a quote for a master bathroom remodel, guest bathroom update, small bathroom remodel, shower update, tub update, tile, flooring, vanities, fixtures, and layout improvements in the Lakeland area.\n\n"
  }];
  return <section data-section-id="552" id="common-questions" className="bg-background py-20 md:py-28 lg:py-32">
      <div className="container mx-auto px-6 lg:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          {}
          <div ref={ref} data-index={0} className={`lg:col-span-5 transition-all duration-700 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
            <div className="lg:sticky lg:top-28">
              <span className="font-mono text-xs uppercase tracking-widest text-muted-foreground">
                — FAQ
              </span>
              <h2 className="font-default mt-6 text-5xl md:text-6xl lg:text-7xl font-semibold tracking-tight text-foreground leading-[0.95]">
                Common
                <br />
                questions.
              </h2>
              <div className="mt-10 flex items-start gap-3 p-5 rounded-lg border border-border bg-card">
                <Mail className="size-5 text-foreground shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm text-muted-foreground">Have questions about your bathroom remodel?</p>
                  <Link to="/Contact" className="font-default text-base font-medium text-foreground hover:text-primary transition-colors">Request a Free Quote</Link>
                </div>
              </div>
            </div>
          </div>

          {}
          <div className="lg:col-span-7">
            <Accordion type="single" collapsible className="w-full">
              {faqs.map((faq, i) => <AccordionItem data-index={i} key={i} value={`item-${i}`} className="border-b border-border first:border-t border-t-0 first:border-t-border">
                  <AccordionTrigger className="group py-7 md:py-8 text-left hover:no-underline data-[state=open]:text-primary [&>svg]:size-6 [&>svg]:text-foreground [&>svg]:transition-transform [&>svg]:duration-300 [&[data-state=open]>svg]:text-primary">
                    <div className="flex flex-col gap-2 pr-4">
                      <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground group-data-[state=open]:text-primary/80">
                        {faq.category}
                      </span>
                      <span className="font-default text-xl md:text-2xl font-semibold tracking-tight leading-snug">
                        {faq.question}
                      </span>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="pb-8 pr-8">
                    <p className="text-base md:text-lg text-muted-foreground leading-relaxed">
                      {faq.answer}
                    </p>
                  </AccordionContent>
                </AccordionItem>)}
            </Accordion>
          </div>
        </div>
      </div>
    </section>;
}